import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { deleteClientAccount, getAllOurAccounts } from "../../../../services/shipping";
import { useParams } from "react-router-dom";
import ThirdPartyShippingAccount from "../../../../components/Shipping/ThirdPartyShippingAccount";

export default function ShippingAccountsTab() {
  const { instanceUniqueId } = useParams();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const res = await getAllOurAccounts({ instanceUniqueId });
      setAccounts(res?.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Shipping Accounts
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            View internal shipping accounts available for fulfillment.
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.7, mt: 0.5 }}>
            These are your company’s carrier accounts used when “Our Account” is selected.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" onClick={() => setAccountModalOpen(true)}>
            Add Account
          </Button>
          <Button variant="contained" onClick={fetchAccounts}>
            Refresh
          </Button>
        </Box>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Account Name</TableCell>
            <TableCell>Courier</TableCell>
            <TableCell>Account #</TableCell>
            <TableCell>Billing Postal/Zip</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(accounts || []).map((account) => (
            <TableRow key={account.id} hover>
              <TableCell sx={{ fontWeight: 700 }}>{account.accountName || "—"}</TableCell>
              <TableCell>{account.courier || "—"}</TableCell>
              <TableCell>{account.accountNumber || "—"}</TableCell>
              <TableCell>{account.postalZipCode || "—"}</TableCell>
              <TableCell align="right">
                <IconButton
                  size="small"
                  aria-label="Edit shipping account"
                  onClick={() => {
                    setEditingAccount(account);
                    setAccountModalOpen(true);
                  }}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="Delete shipping account"
                  onClick={async () => {
                    if (!window.confirm("Delete this shipping account?")) return;
                    try {
                      await deleteClientAccount(account.id);
                      fetchAccounts();
                    } catch (error) {
                      console.error("Failed to delete shipping account", error);
                    }
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {!loading && (accounts || []).length === 0 && (
            <TableRow>
              <TableCell colSpan={5} sx={{ py: 4, textAlign: "center", opacity: 0.7 }}>
                No shipping accounts found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <ThirdPartyShippingAccount
        open={accountModalOpen}
        onClose={() => {
          setAccountModalOpen(false);
          setEditingAccount(null);
        }}
        instanceUniqueId={instanceUniqueId}
        onCreated={fetchAccounts}
        initialData={editingAccount}
      />
    </Box>
  );
}
